/* eslint-disable indent */
import * as React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Label from "../../../components/label";
import { useSharedContext } from "../../../shared/SharedComponentsContext";
import MenuPopover from "../../../components/menu-popover";
import { IconButton, LinearProgress, MenuItem, Stack } from "@mui/material";
import Iconify from "../../../components/iconify";
import { Deal } from "../../../api/bitrix/types/deal";
import { format } from "date-fns";
import { useBitrixContext } from "../../../api/bitrix/context/BitrixContext";
import { calculateDealStageProgress } from "../../../api/bitrix/status";
import { Status } from "../../../api/bitrix/types/status";
import { fCurrency } from "../../../utils/formatNumber";

type Props = {
  row: Deal;
};

export default function OrdersCard({ row }: Props) {
  const { STAGE, CONTACT, COMPANY, OPPORTUNITY, DATE_MODIFY, INVOICE_STATUS } =
    row;

  const { openOrderViewSlider } = useSharedContext();
  const { listFields } = useBitrixContext();

  const [openPopover, setOpenPopover] = React.useState<HTMLElement | null>(
    null,
  );

  const [stageProgress, setStageProgress] = React.useState<number>(0);

  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setOpenPopover(event.currentTarget);
  };

  const handleClosePopover = () => {
    setOpenPopover(null);
  };

  React.useEffect(() => {
    if (STAGE) {
      calculateDealStageProgress(
        STAGE.STATUS_ID,
        listFields.find((f) => f.FIELD_NAME === "DEAL_STAGE")?.LIST as Status[],
      ).then(setStageProgress);
    }
  }, []);

  return (
    <Box sx={{ minWidth: 275 }}>
      <Card
        variant="outlined"
        sx={{ mt: 2, minHeight: 240, position: "relative" }}
        onClick={() => openOrderViewSlider(row)}
      >
        <Box sx={{ position: "absolute", top: 8, right: 8 }}>
          <IconButton
            color={openPopover ? "inherit" : "default"}
            onClick={handleOpenPopover}
          >
            <Iconify icon="eva:more-vertical-fill" />
          </IconButton>
        </Box>
        <React.Fragment>
          <CardContent>
            <Typography
              gutterBottom
              sx={{ color: "text.secondary", fontSize: 14 }}
            >
              Orden
            </Typography>
            <Typography>{fCurrency(Number(OPPORTUNITY))}</Typography>
            <Typography variant="h5" textTransform="capitalize" component="div">
              {`${CONTACT?.NAME} ${CONTACT?.LAST_NAME}`}
            </Typography>
            {COMPANY?.TITLE && (
              <Typography
                textTransform="capitalize"
                sx={{ color: "text.secondary", mb: 1.5 }}
              >
                {COMPANY.TITLE}
              </Typography>
            )}
            {STAGE && (
              <Stack spacing={1} my={3}>
                <Stack direction="row" alignItems="center">
                  <Typography variant="caption" sx={{ flexGrow: 1 }}>
                    {STAGE.NAME}
                  </Typography>
                </Stack>

                <LinearProgress
                  variant="determinate"
                  value={stageProgress}
                  color="primary"
                />
              </Stack>
            )}

            {INVOICE_STATUS && (
              <Label
                variant="soft"
                color={
                  INVOICE_STATUS.NAME === "Paid"
                    ? "success"
                    : INVOICE_STATUS.NAME === "Do not pay"
                    ? "error"
                    : INVOICE_STATUS.NAME === "Past Due"
                    ? "warning"
                    : "primary"
                }
                sx={{ textTransform: "capitalize", mr: 2 }}
              >
                {INVOICE_STATUS.NAME}
              </Label>
            )}
            <Typography variant="caption">
              {format(new Date(DATE_MODIFY), "yyyy-MM-dd HH:mm")}
            </Typography>
          </CardContent>
        </React.Fragment>
      </Card>

      <MenuPopover
        open={openPopover}
        onClose={handleClosePopover}
        arrow="right-top"
        sx={{ width: 140 }}
      >
        <MenuItem
          onClick={() => {
            handleClosePopover();
            openOrderViewSlider(row);
          }}
        >
          <Iconify icon="clarity:details-line" />
          Detalles
        </MenuItem>
      </MenuPopover>
    </Box>
  );
}
